import { OverlayModule } from '@angular/cdk/overlay';
import { NgClass } from '@angular/common';
import { Component, computed, EventEmitter, forwardRef, inject, Input, input, Output, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { NgxFloatUiModule } from 'ngx-float-ui';
import { ApplyPipe } from 'ngxtension/call-apply';

import { LanguageOption, LanguageService } from './language.service';

@Component({
  selector: 'app-language-dropdown',
  imports: [ApplyPipe, LucideAngularModule, NgClass, NgxFloatUiModule, OverlayModule],
  templateUrl: './language-dropdown.component.html',
  styleUrl: './language-dropdown.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => LanguageDropdownComponent),
    },
  ],
})
export class LanguageDropdownComponent implements ControlValueAccessor {
  languageService = inject(LanguageService);
  languages = this.languageService.$languages;
  dropdownVisible = signal(false);
  compact = input(false);
  automationId = computed(() => (this.compact() ? `language-dropdown-compact` : `language-dropdown`));

  @Input() value!: string;
  @Input() name!: string;
  @Input() disabled!: boolean;
  @Input() styleClass!: string;

  @Output() change = new EventEmitter<string>();

  onChange: any = (value: any) => {};
  onTouched: any = () => {};

  writeValue(value: any, hideDropdown: boolean = false): void {
    this.value = value;
    if (!hideDropdown) return;
    this.dropdownVisible.set(false);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
    this.dropdownVisible.set(false);
  }

  onSelect(value: any) {
    if (this.value !== value) {
      this.onChange(value);
      this.onTouched();
    }
    this.writeValue(value, true);
    this.change.emit(this.value);
  }

  getValueName(value: any, options: LanguageOption[]) {
    if (!options || options.length === 0) return undefined;
    return options.find((option) => option.code === value)?.name || 'Select a language';
  }

  getFlag(value: any, options: LanguageOption[]) {
    if (!options || options.length === 0) return undefined;
    return options.find((option) => option.code === value)?.flag || '';
  }
}
